import React, { ReactNode, useCallback, useMemo } from 'react';
import './GenericCardList.scss';
import RestaurantLoader from '../RestaurantLoader/RestaurantLoader.';

interface GenericCardListProps<T> {
  data: T[];
  renderCard: (item: T, onPress?: (item: T) => void) => ReactNode;
  onCardPress?: (item: T) => void;
  keyExtractor?: (item: T, index: number) => string;
  itemsPerRow?: 1 | 2 | 3 | 4;
  containerPadding?: number;
  itemSpacing?: number;
  scrollDirection?: 'vertical' | 'horizontal';
  showVerticalScrollIndicator?: boolean;
  showHorizontalScrollIndicator?: boolean;
  contentContainerStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  // Optional header/footer
  ListHeaderComponent?: ReactNode;
  ListFooterComponent?: ReactNode;
  ListEmptyComponent?: ReactNode;
  // Refresh control
  refreshing?: boolean;
  onRefresh?: () => void;
  // Load more
  onEndReached?: () => void;
  onEndReachedThreshold?: number;
  // View All props
  showViewAll?: boolean;
  onPressViewAll?: () => void;
  viewAllText?: string;
  renderViewAllCard?: () => ReactNode;
}

type ListItem<T> = T | { _isViewAll: true };

export const GenericCardList = <T extends any>({
  data,
  renderCard,
  onCardPress,
  keyExtractor,
  itemsPerRow = 2,
  containerPadding = 4,
  itemSpacing = 12,
  scrollDirection = 'vertical',
  showVerticalScrollIndicator = true,
  showHorizontalScrollIndicator = true,
  contentContainerStyle,
  style,
  ListHeaderComponent,
  ListFooterComponent,
  ListEmptyComponent,
  refreshing,
  onRefresh,
  onEndReached,
  onEndReachedThreshold = 0.5,
  showViewAll = false,
  onPressViewAll,
  viewAllText = "Voir tout",
  renderViewAllCard
}: GenericCardListProps<T>) => {

  const listData: ListItem<T>[] = useMemo(() => {
    if (showViewAll && scrollDirection === 'horizontal' && data.length > 0) {
      return [...data, { _isViewAll: true } as ListItem<T>];
    }
    return data;
  }, [data, showViewAll, scrollDirection]);

  const isViewAllItem = (item: ListItem<T>): item is { _isViewAll: true } => {
    return typeof item === 'object' && item !== null && '_isViewAll' in item;
  };

  const renderDefaultViewAllCard = useCallback(() => (
    <div
      className="view-all-card"
      onClick={onPressViewAll}
    >
      <div className="view-all-content">
        <span className="view-all-text">{viewAllText}</span>
        <span className="view-all-arrow">→</span>
      </div>
    </div>
  ), [onPressViewAll, viewAllText]);

  const getDefaultKeyExtractor = useCallback((item: ListItem<T>, index: number): string => {
    if (isViewAllItem(item)) {
      return 'view-all-item';
    }

    if (keyExtractor) {
      return keyExtractor(item as T, index);
    }

    // Try to extract common id fields
    if (typeof item === 'object' && item !== null) {
      const itemObj = item as any;
      if ('id' in itemObj) return String(itemObj.id);
      if ('key' in itemObj) return String(itemObj.key);
      if ('_id' in itemObj) return String(itemObj._id);
    }

    return index.toString();
  }, [keyExtractor]);

  // Handle scroll to detect end reached
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (!onEndReached) return;

    const element = e.currentTarget;
    const threshold = onEndReachedThreshold || 0.5;

    if (scrollDirection === 'horizontal') {
      const { scrollLeft, scrollWidth, clientWidth } = element;
      const scrollPercentage = (scrollLeft + clientWidth) / scrollWidth;

      if (scrollPercentage >= 1 - threshold) {
        onEndReached();
      }
    } else {
      const { scrollTop, scrollHeight, clientHeight } = element;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      if (scrollPercentage >= 1 - threshold) {
        onEndReached();
      }
    }
  }, [onEndReached, onEndReachedThreshold, scrollDirection]);

  // Normalize components
  const normalizeComponent = (component?: ReactNode) => {
    if (component === undefined || component === null) return null;
    // Check if component is a function (function component or render prop)
    if (typeof component === 'function') {
      // TypeScript: treat as a function returning ReactNode
      return (component as () => ReactNode)();
    }
    if (React.isValidElement(component)) return component;
    if (typeof component === 'string' || typeof component === 'number') {
      return <div className="normalized-component">{component}</div>;
    }
    return null;
  };

  // Generate CSS classes
  const containerClasses = [
    'generic-card-list',
    `direction-${scrollDirection}`,
    `items-per-row-${itemsPerRow}`,
    showVerticalScrollIndicator && scrollDirection === 'vertical' ? 'show-vertical-scroll' : '',
    showHorizontalScrollIndicator && scrollDirection === 'horizontal' ? 'show-horizontal-scroll' : '',
  ].filter(Boolean).join(' ');

  // Generate container styles
  const containerStyles: React.CSSProperties = {
    '--container-padding': `${containerPadding}px`,
    '--item-spacing': `${itemSpacing}px`,
    '--items-per-row': itemsPerRow,
    ...style,
  } as React.CSSProperties;

  const contentStyles: React.CSSProperties = {
    ...contentContainerStyle,
  };

  // Render loading state
  if (refreshing) {
    return (
      <div className={containerClasses} style={containerStyles}>

        <RestaurantLoader
          message="Chargement des éléments..."
          size="large"
          variant='dots'
        />
      </div>
    );
  }

  // Render empty state
  if (data.length === 0) {
    return (
      <div className={containerClasses} style={containerStyles}>
        {normalizeComponent(ListHeaderComponent)}
        {normalizeComponent(ListEmptyComponent) || (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <div className="empty-title">Aucun élément</div>
            <div className="empty-message">Il n'y a aucun élément à afficher pour le moment.</div>
          </div>
        )}
        {normalizeComponent(ListFooterComponent)}
      </div>
    );
  }

  return (
    <div
      className={containerClasses}
      style={containerStyles}
      onScroll={handleScroll}
    >
      {normalizeComponent(ListHeaderComponent)}

      <div className="card-list-content" style={contentStyles}>
        {listData.map((item, index) => {
          const key = getDefaultKeyExtractor(item, index);

          // Handle View All card
          if (isViewAllItem(item)) {
            const viewAllCard = renderViewAllCard ? renderViewAllCard() : renderDefaultViewAllCard();
            return (
              <div key={key} className="card-item view-all-item">
                {viewAllCard}
              </div>
            );
          }

          // Handle regular items
          const cardElement = renderCard(item as T, onCardPress);

          return (
            <div key={key} className="card-item">
              {cardElement}
            </div>
          );
        })}
      </div>

      {normalizeComponent(ListFooterComponent)}

      {onRefresh && (
        <button className="refresh-button" onClick={onRefresh}>
          Actualiser
        </button>
      )}
    </div>
  );
};